import { WidgetUUID } from '@/common/utils';
import { useDashboardViewStore } from '@/dashboard/stores/dashboard-view';
import { DASHBOARD } from '@/common/define/userEnv/dashboard.env.define';
import { storeToRefs } from 'pinia';
import {
  getV11DashboardsEnv,
  postV11DashboardsEnv,
} from '@/api-client/dashboard/queries/dashboard-env-controller/dashboard-env-controller';
import type { CreateDashboardEnvRequest, UserEnvValue } from '@/api-client/dashboard/model';

export type DashboardUserEnvKeys = (typeof DASHBOARD)[keyof typeof DASHBOARD];

export const useDashboardUserEnv = () => {
  const { monitoringDashboard } = storeToRefs(useDashboardViewStore());

  const getWidgetUserEnvKey = (
    envKey: DashboardUserEnvKeys,
    widgetUUID: WidgetUUID,
    dashboardId?: number,
  ) => {
    const currentDashboardId = monitoringDashboard.value.dashboardId ?? -1;
    const targetId = dashboardId || currentDashboardId;
    return `${envKey}_${targetId}_${widgetUUID}` as const;
  };

  /**
   * 대시보드 env 키에서 `_{dashboardId}` (widget 단위는 `_{id}_{UUID}`,
   * 대시보드 단위는 `_{id}`로 종료)를 새로운 ID로 치환.
   */
  const rewriteDashboardIdInKey = (key: string, fromId: number, toId: number): string | null => {
    const pattern = new RegExp(`_${fromId}(?=_|$)`);
    if (!pattern.test(key)) return null;
    return key.replace(pattern, `_${toId}`);
  };

  const fetchSourceDashboardEnv = async (
    dashboardId: number,
  ): Promise<{ globalData: UserEnvValue[]; customData: UserEnvValue[] }> => {
    // 복제 플로우에서는 조회 실패를 조용히 빈 결과로 처리하지 않고 caller의
    // onError 로 전파해야 한다 (env 누락을 성공처럼 표시하는 것을 방지).
    const [globalRes, customRes] = await Promise.all([
      getV11DashboardsEnv({ dashboardId, global: true }),
      getV11DashboardsEnv({ dashboardId, global: false }),
    ]);

    return {
      globalData: globalRes.data?.data ?? [],
      customData: customRes.data?.data ?? [],
    };
  };

  const buildCopyPayload = (
    items: UserEnvValue[],
    fromId: number,
    toId: number,
  ): CreateDashboardEnvRequest[] => {
    return items.reduce<CreateDashboardEnvRequest[]>((acc, { key, value }) => {
      if (!key || value === undefined) return acc;
      const newKey = rewriteDashboardIdInKey(key, fromId, toId);
      if (!newKey) return acc;
      acc.push({ dashboardId: toId, key: newKey, value });
      return acc;
    }, []);
  };

  const copyDashboardUserEnv = async (targetDashboardId: number, newDashboardId: number) => {
    // 소스 대시보드 env를 서버에서 직접 조회 (store 의존성 없음 → DashboardList/View 양 경로에서 동작)
    const { globalData, customData } = await fetchSourceDashboardEnv(targetDashboardId);

    const globalPayload = buildCopyPayload(globalData, targetDashboardId, newDashboardId);
    const customPayload = buildCopyPayload(customData, targetDashboardId, newDashboardId);

    const tasks: Promise<unknown>[] = [];
    if (globalPayload.length > 0) {
      tasks.push(postV11DashboardsEnv(globalPayload, { global: true }));
    }
    if (customPayload.length > 0) {
      tasks.push(postV11DashboardsEnv(customPayload, { global: false }));
    }
    await Promise.all(tasks);
  };

  return { getWidgetUserEnvKey, copyDashboardUserEnv };
};
